import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { RequestServiceBase } from './RequestServiceBase';
import { Observable } from 'rxjs';
import { response } from '../model/content-response';
import { CodeResponse } from '../model/code-response';
import { blurbQueryParam, blurbResponse, footnoteRefResponse, newBlurbResponse } from '../model/blurb-response';
import { Franchise, IbisReport, pncResponse, PreparedStatement, removeResponse } from '../model';
import { IbisBlurbResponse, ibisQueryParam, ibisResponse } from '../model/ibis-models';
import { CensusGeo } from '../model/census.geo';
import { environment } from '../research.env';


@Injectable({
  providedIn: 'root'
})

export class HttpService extends RequestServiceBase {

  constructor(public http: HttpClient) {
    super(http);
  }

  // #region "Location module"

  // submitLocation() - Do post request when user submit location.
  submitLocation(reportId: string): Observable<response> {
    return this.post(`${environment.API_URL}${environment.ENDPOINT_PREFIX}/submit?id=${reportId}`, null, 1);
  }

  // updateLocationModification() - Do post request when user submit location.
  updateLocationModification(contentRequest: any): Observable<any> {
    return this.http
      .put(`${environment.API_URL}${environment.ENDPOINT_PREFIX}/submit`, contentRequest, this.getOptionsPlain());
  }

  // getAddressBasedOnZip() - Do get request to get city and state based on zip code.
  getAddressBasedOnZip(code: number): Observable<CodeResponse> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX + '/locations/zip/' + code);
  }

  // #endregion

  // #region 'Blurb module'

  // getBlurbsList() - Do get request to get list of all blurbs.
  getBlurbsList(searchObj: blurbQueryParam): Observable<blurbResponse> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX + '/ps?query=' + searchObj.query + '&order=' + searchObj.order + '&field=' + searchObj.field + '&page=' + searchObj.page);
  }

  // addBlurbItem() - Do put request to add new blurb item.
  addBlurbItem(contentRequest: PreparedStatement): Observable<newBlurbResponse> {
    if (contentRequest.ID) {
      return this.post(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ps/' + contentRequest.ID, contentRequest);
    } else {
      return this.put(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ps', contentRequest);
    }
  }

  // removeBlurbItem() - Do DELETE request to remove blurb item.
  removeBlurbItem(contentRequest: PreparedStatement): Observable<removeResponse> {
    return this.delete(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ps/' + contentRequest.ID, contentRequest);
  }

  // getFootnoteRef() - Do get request to get latest footnote ref.
  getFootnoteRef(): Observable<footnoteRefResponse> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ps/footnotes');
  }

  // #endregion

  // #region 'IBIS module'

  // getIbisList() - Do get request to get list of all ibis.
  getIbisList(searchObj: ibisQueryParam): Observable<ibisResponse> {
    return this.get(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ibis?query=' + searchObj.query + '&order=' + searchObj.order + '&field=' + searchObj.field + '&page=' + searchObj.page);
  }

  // createIbisItem() - Do post request to create new ibis item.
  createIbisItem(contentRequest: FormData): Observable<ibisResponse> {
    return this.post(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ibis', contentRequest, this.getHeaderType().MULTIPART_DATA);
  }

  // createIbisReport() - Do post request to create new ibis report.
  createIbisDataReport(report: FormData): Observable<any> {
    return this.post(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ibis/data', report, this.getHeaderType().MULTIPART_DATA);
  }

  // updateIbisDataReport() - Do post request to update ibis report.
  updateIbisDataReport(id: number, report: FormData): Observable<any> {
    return this.post(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ibis/data/' + id, report, this.getHeaderType().MULTIPART_DATA);
  }

  // removeIbisItem() - Do DELETE request to remove blurb item.
  removeIbisItem(ibisID: number): Observable<removeResponse> {
    return this.delete(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ibis/' + ibisID, ibisID);
  }

  // getIbisReport() - Do get request to get list of all ibis report.
  getIbisReport(): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ibis');
  }

  // getIbisReport() - Do get request to get list of all ibis report.
  getIbisReportById(id: number): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ibis/'+ id);
  }

  // downloadIbisFile() - Do get request to download ibis file.
  downloadIbisFile(contentRequest: any): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ibis/' + contentRequest['ID']);
  }

  // createIbisReportBlurb() - Do POST request to create ibis external blurb item of ibis report.
  createIbisReportBlurb(ibisID: number): Observable<IbisBlurbResponse> {
    return this.post(environment.API_URL  + environment.ENDPOINT_PREFIX + '/ibis/' + ibisID + '/ps', null);
  }

  // removeIbisReportBlurb() - Do DELETE request to remove blurb item of ibis report.
  removeIbisReportBlurb(ibisID: number, psId: string): Observable<removeResponse> {
    return this.delete(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ibis/' + ibisID + '/ps/' + psId, psId);
  }

  // Do get request to get latest ibis report.
  getLatestIbisReport(code: string): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX  + '/ibis/code/'+ code);
  }

  // #endregion

  // #region 'Other'

  private parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = Observable.create((observer: any) => {
      reader.onloadend = () :any => {
        let returnError: any = err;
        // noinspection TypeScriptValidateTypes
        returnError.json = JSON.parse(<any>reader.result);
        observer.error(returnError);
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  private getOptionsPlain(): any {
    return {
      responseType: 'blob' as 'blob'
    };
  }

  // getHeaderType() - Get type of the header
  private getHeaderType(): any {
    const HeaderType = {
      APPLICATION_JSON: 0,
      MULTIPART_DATA: 1
    };
    return HeaderType;
  }

  // rebuildTable() - Do post request to rebuild table.
  rebuildTable(contentRequest: CensusGeo[], tableType: string): Observable<response> {
    return this.put(`${environment.API_URL}${environment.ENDPOINT_PREFIX}/census/` + tableType.toLowerCase(), contentRequest);
  }

  // #endregion

  // #region 'Google'

  // checkStreetViewAvailable() - Get latitude and longitude based on address.
  checkStreetViewAvailable(params: string): Observable<any> {
    return this.get(environment.API_URL + environment.GOOGLE_ENDPOINT_PREFIX + '/streetview/metadata?' + params);
  }

  // getStreetViewImage() - Get street view image based on given parameter.
  getStreetViewImage(params: string): Observable<any> {
    return this.getImage(environment.API_URL + environment.GOOGLE_ENDPOINT_PREFIX + '/streetview?' + params);
  }

  // getMapImage() - Get map image based on given parameter.
  getMapImage(params: string): Observable<any> {
    return this.getImage(environment.API_URL + environment.GOOGLE_ENDPOINT_PREFIX + '/staticmap?' + params);
  }

  // getGeocode() - Get geocode of address.
  getGeocode(params: string): Observable<any> {
    return this.get(environment.API_URL + environment.GOOGLE_ENDPOINT_PREFIX + '/geocode?address=' + params);
  }

  // #endregion

  // #region "PNC Report"

  // getPncReportList() - Do get request to get list of all report.
  getPncReportList(searchObj: ibisQueryParam): Observable<pncResponse> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX + '/pnc?query=' + searchObj.query + '&order=' + searchObj.order + '&field=' + searchObj.field + '&page=' + searchObj.page);
  }

  // createPncItem() - Do post request to create new pnc item.
  createPncItem(contentRequest: FormData): Observable<pncResponse> {
    return this.post(environment.API_URL + environment.ENDPOINT_PREFIX + '/pnc', contentRequest, this.getHeaderType().MULTIPART_DATA);
  }

  // createPncReport() - Do post request to create new pnc report.
  createPncReport(report: IbisReport): Observable<pncResponse> {
    return this.post(environment.API_URL + environment.ENDPOINT_PREFIX + '/pnc/data', report);
  }

  // updatePncReport() - Do post request to update ibis report.
  updatePncReport(report: IbisReport): Observable<pncResponse> {
    return this.post(environment.API_URL + environment.ENDPOINT_PREFIX + '/pnc/data/' + report.ID, report);
  }

  // removePncItem() - Do DELETE request to remove pnc report.
  removePncItem(ibisID: number): Observable<removeResponse> {
    return this.delete(environment.API_URL + environment.ENDPOINT_PREFIX + '/pnc/' + ibisID, ibisID);
  }

  // getPncReports() - Do get request to get list of all pnc report.
  getPncReports(): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX + '/pnc');
  }

  // getPncReportById() - Do get request to get list of all ibis report.
  getPncReportById(id: number): Observable<any> {
    return this.get(environment.API_URL  + environment.ENDPOINT_PREFIX + '/pnc/'+ id);
  }

  //getFranchiseById() - Do get request to get single Franchise
  getFranchiseById(id: string): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX + '/franchise/' + id);
  }

  // getFranchiseList(page, query) - Do get request to get list of all Franchise
  getFranchiseList(page: number, query?: string): Observable<any> {
    return this.get(environment.API_URL + environment.ENDPOINT_PREFIX + `/franchise?page=${page}${query ? '&query=' + encodeURIComponent(query) : ''}`);
  }

  // createFranchise() - Do post request to create new Franchise report.
  createFranchise(franchise: Franchise): Observable<any> {
    return this.post(environment.API_URL + environment.ENDPOINT_PREFIX + '/franchise', franchise);
  }

  // createFranchise() - Do post request to create new Franchise report.
  updateFranchise(franchise: Franchise): Observable<any> {
    return this.put(environment.API_URL + environment.ENDPOINT_PREFIX + '/franchise', franchise);
  }

  //deleteFranchiseById() - Do get request to delete Franchise
  deleteFranchiseById(id: string): Observable<any> {
    return this.delete(environment.API_URL + environment.ENDPOINT_PREFIX + '/franchise/' + id, id);
  }

  //getResearchContent() - Do get request of Research saved Content
  getResearchContent(reportId: string): Observable<response> {
    return this.get(`${environment.API_URL}${environment.ENDPOINT_PREFIX}/content/${reportId}`);
  }

  saveContent(reportId: string, data: any): Observable<response> {
    return this.post(`${environment.API_URL}${environment.ENDPOINT_PREFIX}/content/${reportId}`, data, 1);
  }
  
  // #endregion
}
